import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, StatusBadge, EmptyState } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { Briefcase, ExternalLink, Check, X as XIcon, PlayCircle, StopCircle } from 'lucide-react';
import { toast } from 'sonner';
import { statusLabel, serviceTypeLabel, meetingToolLabel } from '../../lib/constants';

const InterpreterJobs = () => {
  const [jobs, setJobs] = useState([]);

  const load = async () => { setJobs((await api.get('/jobs')).data); };
  useEffect(() => { load(); }, []);

  const respond = async (jobId, action) => {
    try {
      await api.post(`/jobs/${jobId}/respond`, { action });
      toast.success(action === 'accept' ? 'Auftrag bestätigt' : 'Auftrag abgelehnt');
      load();
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const startMeeting = async (jobId) => {
    try {
      await api.post(`/jobs/${jobId}/meeting/start`);
      toast.success('Meeting gestartet - Zeitmessung läuft');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    }
  };

  const stopMeeting = async (jobId) => {
    if (!window.confirm('Meeting jetzt beenden und Abrechnung erzeugen?')) return;
    try {
      const res = await api.post(`/jobs/${jobId}/meeting/stop`);
      toast.success(`Meeting beendet - ${res.data.billed_duration_minutes} Min / ${res.data.billed_interpreter_amount} €`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    }
  };

  const requested = jobs.filter(j => j.status === 'dolmetscher_angefragt');
  const active = jobs.filter(j => ['dolmetscher_bestaetigt', 'bestaetigt', 'meetinglink_versendet', 'in_durchfuehrung'].includes(j.status));
  const others = jobs.filter(j => !['dolmetscher_angefragt', 'dolmetscher_bestaetigt', 'bestaetigt', 'meetinglink_versendet', 'in_durchfuehrung'].includes(j.status));

  return (
    <AppLayout>
      <PageHeader title="Meine Aufträge" description="Anfragen, bestätigte und vergangene Termine" />

      {requested.length > 0 && (
        <section className="mb-8">
          <h2 className="font-heading text-base font-medium text-slate-900 mb-3">Offene Anfragen</h2>
          <div className="bg-white border border-slate-200 rounded-md card-subtle divide-y divide-slate-100">
            {requested.map(j => (
              <div key={j.id} className="p-4 flex items-center justify-between" data-testid={`requested-job-${j.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{j.source_language} → {j.target_language} · {serviceTypeLabel(j.service_type)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{j.date} {j.time} · {j.duration} Min · {j.specialization || 'Allgemein'}</div>
                  {j.notes && <div className="text-xs text-slate-600 mt-1">{j.notes}</div>}
                  <div className="text-xs text-slate-700 font-mono mt-1">Honorar: {j.interpreter_price?.toFixed(2)} €</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => respond(j.id, 'accept')} data-testid={`accept-${j.id}`} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md px-3 py-1.5 text-xs">
                    <Check className="w-3 h-3" /> Bestätigen
                  </button>
                  <button onClick={() => respond(j.id, 'reject')} data-testid={`reject-${j.id}`} className="inline-flex items-center gap-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-md px-3 py-1.5 text-xs">
                    <XIcon className="w-3 h-3" /> Ablehnen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {active.length > 0 && (
        <section className="mb-8">
          <h2 className="font-heading text-base font-medium text-slate-900 mb-3">Bevorstehend / Laufend</h2>
          <div className="bg-white border border-slate-200 rounded-md card-subtle divide-y divide-slate-100">
            {active.map(j => {
              const isRunning = j.status === 'in_durchfuehrung';
              return (
                <div key={j.id} className="p-4 flex items-center justify-between gap-3" data-testid={`active-job-${j.id}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">{j.source_language} → {j.target_language} · {j.date} {j.time}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Geplant: {j.duration} Min · Mind.: {j.min_duration || j.duration} Min
                      {isRunning && j.actual_start_time && <span className="ml-2 text-emerald-700 font-medium">· gestartet {new Date(j.actual_start_time).toLocaleTimeString('de-DE')}</span>}
                    </div>
                    {j.meeting_link && (
                      <a href={j.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0F4C81] hover:underline mt-1" data-testid={`meeting-link-${j.id}`}>
                        <ExternalLink className="w-3 h-3" /> {({google_meet:'Google Meet',microsoft_teams:'Teams',zoom:'Zoom',manual:'Link'})[j.meeting_provider] || meetingToolLabel(j.meeting_tool) || 'Meeting öffnen'}
                      </a>
                    )}
                  </div>
                  <div>
                    {!isRunning ? (
                      <button onClick={() => startMeeting(j.id)} data-testid={`start-meeting-${j.id}`} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md px-3 py-2 text-sm">
                        <PlayCircle className="w-4 h-4" /> Meeting starten
                      </button>
                    ) : (
                      <button onClick={() => stopMeeting(j.id)} data-testid={`stop-meeting-${j.id}`} className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md px-3 py-2 text-sm">
                        <StopCircle className="w-4 h-4" /> Meeting beenden
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-heading text-base font-medium text-slate-900 mb-3">Verlauf</h2>
        {others.length === 0 && requested.length === 0 && active.length === 0 ? (
          <EmptyState icon={Briefcase} title="Noch keine Aufträge" description="Sie werden benachrichtigt, sobald Ihnen Aufträge angeboten werden." />
        ) : others.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-4">Keine weiteren Aufträge</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md card-subtle overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Datum</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Sprache</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Einsatz</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Dauer/Honorar</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {others.map(j => (
                  <tr key={j.id} className="hover:bg-slate-50">
                    <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-900">{j.date} {j.time}</td>
                    <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{j.source_language} → {j.target_language}</td>
                    <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{serviceTypeLabel(j.service_type)}</td>
                    <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700 font-mono">
                      {j.billed_duration_minutes ? `${j.billed_duration_minutes} Min · ${j.billed_interpreter_amount?.toFixed(2)} €` : `${j.interpreter_price?.toFixed(2)} €`}
                    </td>
                    <td className="border-b border-slate-100 py-3 px-4"><StatusBadge status={j.status} label={statusLabel(j.status)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AppLayout>
  );
};

export default InterpreterJobs;

